import { create } from 'zustand'
import type { BleStatus, BleDevice, SensorPlacement } from '@/features/ble/types'

interface BleStoreState {
  status: BleStatus
  device: BleDevice | null
  error: string | null
  reconnectAttempt: number
  devices: Record<string, BleDeviceEntry>
  deviceOrder: string[]
  setStatus: (status: BleStatus) => void
  setDevice: (device: BleDevice | null) => void
  setError: (error: string | null) => void
  setReconnectAttempt: (attempt: number) => void
  upsertDevice: (device: BleDeviceEntry) => void
  removeDevice: (deviceId: string) => void
  setDeviceStatus: (deviceId: string, status: BleStatus) => void
  setDeviceError: (deviceId: string, error: string | null) => void
  setDeviceReconnectAttempt: (deviceId: string, attempt: number) => void
  setDevicePlacement: (deviceId: string, placement: SensorPlacement) => void
  setDeviceHz: (deviceId: string, hz: number) => void
  reset: () => void
}

export interface BleDeviceEntry {
  id: string
  name: string
  status: BleStatus
  error: string | null
  reconnectAttempt: number
  placement: SensorPlacement
  hz: number
}

const initialState = {
  status: 'idle' as BleStatus,
  device: null,
  error: null,
  reconnectAttempt: 0,
  devices: {} as Record<string, BleDeviceEntry>,
  deviceOrder: [] as string[],
}

function summarizeStatus(devices: Record<string, BleDeviceEntry>): BleStatus {
  const values = Object.values(devices)
  if (values.some((device) => device.status === 'error')) return 'error'
  if (values.some((device) => device.status === 'reconnecting')) return 'reconnecting'
  if (values.some((device) => device.status === 'connecting')) return 'connecting'
  if (values.some((device) => device.status === 'scanning')) return 'scanning'
  if (values.some((device) => device.status === 'connected')) return 'connected'
  if (values.some((device) => device.status === 'disconnected')) return 'disconnected'
  return 'idle'
}

export const useBleStore = create<BleStoreState>((set) => ({
  ...initialState,
  setStatus: (status) => set({ status, error: null }),
  setDevice: (device) => set({ device }),
  setError: (error) => set({ error, status: 'error' }),
  setReconnectAttempt: (reconnectAttempt) => set({ reconnectAttempt }),
  upsertDevice: (device) =>
    set((state) => {
      const devices = { ...state.devices, [device.id]: device }
      const deviceOrder = state.deviceOrder.includes(device.id)
        ? state.deviceOrder
        : [...state.deviceOrder, device.id]

      return {
        devices,
        deviceOrder,
        status: summarizeStatus(devices),
        device: { id: device.id, name: device.name },
        error: device.error,
        reconnectAttempt: device.reconnectAttempt,
      }
    }),
  removeDevice: (deviceId) =>
    set((state) => {
      const devices = { ...state.devices }
      delete devices[deviceId]
      const deviceOrder = state.deviceOrder.filter((id) => id !== deviceId)
      const first = deviceOrder[0] ? devices[deviceOrder[0]] : null

      return {
        devices,
        deviceOrder,
        status: summarizeStatus(devices),
        device: first ? { id: first.id, name: first.name } : null,
        error: first?.error ?? null,
        reconnectAttempt: first?.reconnectAttempt ?? 0,
      }
    }),
  setDeviceStatus: (deviceId, status) =>
    set((state) => {
      const existing = state.devices[deviceId]
      if (!existing) return state
      const devices = {
        ...state.devices,
        [deviceId]: {
          ...existing,
          status,
        },
      }

      return {
        devices,
        status: summarizeStatus(devices),
      }
    }),
  setDeviceError: (deviceId, error) =>
    set((state) => {
      const existing = state.devices[deviceId]
      if (!existing) return state
      const devices = {
        ...state.devices,
        [deviceId]: {
          ...existing,
          error,
          status: error ? 'error' : existing.status,
        },
      }

      return {
        devices,
        status: summarizeStatus(devices),
        error,
      }
    }),
  setDeviceReconnectAttempt: (deviceId, reconnectAttempt) =>
    set((state) => {
      const existing = state.devices[deviceId]
      if (!existing) return state
      const devices = {
        ...state.devices,
        [deviceId]: {
          ...existing,
          reconnectAttempt,
        },
      }

      return {
        devices,
        reconnectAttempt,
      }
    }),
  setDevicePlacement: (deviceId, placement) =>
    set((state) => {
      const existing = state.devices[deviceId]
      if (!existing) return state
      return {
        devices: {
          ...state.devices,
          [deviceId]: {
            ...existing,
            placement,
          },
        },
      }
    }),
  setDeviceHz: (deviceId, hz) =>
    set((state) => {
      const existing = state.devices[deviceId]
      if (!existing) return state
      return {
        devices: {
          ...state.devices,
          [deviceId]: {
            ...existing,
            hz,
          },
        },
      }
    }),
  reset: () => set({ ...initialState, status: 'disconnected' }),
}))
